// Raster only — SVG is active content and these images end up on public pages.
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024

export function assertUploadableImage(file: Blob): void {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) throw new Error('Images must be JPG, PNG, GIF, or WebP.')
  if (file.size > MAX_IMAGE_BYTES) throw new Error('Images must be under 5 MB.')
}

/** File extension from a MIME type or a path/file name. */
export function imageExtension(typeOrName: string): string {
  const fromType = /^image\/(jpeg|png|gif|webp)$/.exec(typeOrName)?.[1]
  if (fromType) return fromType === 'jpeg' ? 'jpg' : fromType
  const ext = (typeOrName.split('.').pop() || '').toLowerCase()
  if (ext === 'jpeg') return 'jpg'
  return ['jpg', 'png', 'gif', 'webp'].includes(ext) ? ext : 'jpg'
}

/**
 * Downscale a picked image to at most `max` px on its long edge, as JPEG.
 * GIFs pass through untouched so animation survives.
 */
export async function prepareImage(file: File, max = 1600, quality = 0.82): Promise<Blob> {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) throw new Error('Images must be JPG, PNG, GIF, or WebP.')
  if (file.type === 'image/gif') {
    assertUploadableImage(file)
    return file
  }
  const url = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image()
      el.onload = () => resolve(el)
      el.onerror = () => reject(new Error('That image could not be read.'))
      el.src = url
    })
    const scale = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale))
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale))
    const ctx = canvas.getContext('2d')
    if (!ctx) return file
    ctx.fillStyle = '#FDF8F3' // flatten transparency onto paper (Burrow's card), not black
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality))
    const out = blob ?? file
    assertUploadableImage(out)
    return out
  } finally {
    URL.revokeObjectURL(url)
  }
}

export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the image.'))
    reader.readAsDataURL(blob)
  })
}
